import { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import {
  Modal as AntModal,
  Button,
  Select,
  Input,
  Form,
  Typography,
  Col,
  Divider,
  notification
} from 'antd'
import * as Icon from '@ant-design/icons'
import { useForm, Controller, FormProvider, get } from 'react-hook-form'

import { useApi } from 'admin/lib/Api'

import { useFieldOptions } from './useFieldOptions'
import { MessagePresets } from './MessagePresets'
import { colours } from '@qflow/theme'

export function AddInfractionModal({ open, onClose }) {
  const { accountId, projectId, moduleName, recordId } = useParams()

  const [submitting, setSubmitting] = useState(false)
  const [submitDisabled, setSubmitDisabled] = useState(false)
  const [messagePresetsOptions, setMessagePresetsOptions] = useState([])
  const [additionalNote, setAdditionalNote] = useState()

  //
  // Form setup & values

  const form = useForm({
    defaultValues: {
      fieldName: '',
      lineItems: [],
      message: '',
      messagePresets: ''
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError'
  })
  const { watch, getValues, setValue, reset } = form
  const selectedField = watch('fieldName', '')

  //
  // Calculated Data

  const { fieldOptions, selectedFieldOption } = useFieldOptions(selectedField)

  // Filter out any fields that do not have preset messages from the list of options
  const filteredFieldOptions = fieldOptions.filter(
    option =>
      MessagePresets[moduleName][option.path.split('.').pop()]?.length > 0
  )

  const currentInfractions = useMemo(() => {
    return selectedFieldOption.infractions ?? []
  }, [selectedFieldOption.infractions])

  function useOpenFocus(open, setValue, options) {
    const lastOpenRef = useRef(open)
    const firstFieldRef = useRef()
    useEffect(() => {
      if (open && open !== lastOpenRef.current) {
        const current = document.activeElement

        if (current && current.getAttribute('name')) {
          const name = current.getAttribute('name')

          if (options.some(fo => fo.path === name)) {
            setValue('fieldName', name, {
              shouldDirty: false,
              shouldValidate: true
            })
            handleFieldNameChange(name)
          }
        }

        setTimeout(() => firstFieldRef.current?.focus?.(), 0)
      }

      lastOpenRef.current = open
    }, [options, open, setValue])

    return { firstFieldRef }
  }

  //
  // Effects

  const { firstFieldRef } = useOpenFocus(open, setValue, filteredFieldOptions)

  //
  // Callbacks

  const clearForm = useCallback(() => {
    setAdditionalNote()
    setMessagePresetsOptions([])
    reset()
    onClose()
  }, [reset, onClose])

  const api = useApi()
  const onSubmit = useCallback(
    async formData => {
      setSubmitting(true)
      setSubmitDisabled(true)

      const [, status, error] = await api.createManualInfraction(
        accountId,
        projectId,
        moduleName,
        recordId,
        _calculatePayload(formData, selectedFieldOption.infractionOpts)
      )

      setSubmitDisabled(false)
      if (error) {
        AntModal.error({
          title: 'Error Creating Infraction',
          content: (
            <>
              <p>Error, you may wish to try again. </p>
              <p>{`Details: ${status}: ${error}`}</p>
            </>
          )
        })
      } else {
        clearForm()
      }
    },
    [
      api,
      accountId,
      projectId,
      moduleName,
      recordId,
      selectedFieldOption.infractionOpts,
      clearForm
    ]
  )

  const validateDeletion = useCallback(() => {
    if (
      currentInfractions.filter(infraction => infraction.isPending).length < 1
    ) {
      AntModal.error({
        title: 'No Deletable Infractions',
        content: (
          <p>There are no pending infractions on this field to delete.</p>
        )
      })
      return false
    }
    return true
  }, [currentInfractions])

  const handleDelete = useCallback(async () => {
    setSubmitDisabled(true)
    if (!validateDeletion()) {
      setSubmitDisabled(false)
      return
    }
    const doDeletion = await getUserConfirmationDeleteInfraction()
    if (!doDeletion) {
      setSubmitDisabled(false)
      return
    }
    const [, status, error] = await api.deletePendingInfraction(
      accountId,
      projectId,
      moduleName,
      recordId,
      currentInfractions[0]?.fieldName
    )
    if (error) {
      AntModal.error({
        title: 'Error Deleting Infraction',
        content: (
          <>
            <p>Could not delete infraction.</p>
            <p>
              {status === '404'
                ? 'Infractions could not be found, they may have already been deleted. Please refresh the page.'
                : `Details: ${status}: ${error}`}
            </p>
          </>
        )
      })
    } else {
      clearForm()
      notification.success({
        message: `🗑️ Infraction deleted`,
        duration: 6
      })
    }
    setSubmitDisabled(false)
  }, [
    accountId,
    api,
    moduleName,
    projectId,
    recordId,
    clearForm,
    currentInfractions,
    validateDeletion
  ])

  const handleCancel = () => {
    clearForm()
  }

  const handleFieldNameChange = e => {
    setValue('fieldName', e, { shouldValidate: true })
    // Clear message field
    setValue('messagePresets', '', { shouldValidate: false })
    setValue('message', '', { shouldValidate: false })

    // Look for Field Name Message Preset
    const fieldNameLastPart = e.split('.').pop()
    const messagePresetsFilters = MessagePresets[moduleName][fieldNameLastPart]

    if (messagePresetsFilters && messagePresetsFilters.length > 0) {
      setMessagePresetsOptions(
        messagePresetsFilters.map(messageOption => {
          return {
            value: messageOption.message,
            label: messageOption.shortName || messageOption.message
          }
        })
      )

      if (messagePresetsFilters.length === 1) {
        // If only 1 preset copy message into message field
        setValue('message', messagePresetsFilters[0].message, {
          shouldValidate: true
        })
        setValue(
          'messagePresets',
          messagePresetsFilters[0].shortName ||
            messagePresetsFilters[0].message,
          {
            shouldValidate: true
          }
        )
      }
    } else {
      setMessagePresetsOptions([])
    }
    setAdditionalNote()
  }

  const handleMessagePresetsChange = e => {
    setValue('message', e, { shouldValidate: true })
    // If selected message has a related note show this.
    const fieldNameLastPart = getValues('fieldName').split('.').pop()
    const temp = MessagePresets[moduleName][fieldNameLastPart].find(
      obj => obj.message === e
    )
    if (temp.note) {
      setAdditionalNote(MessagePresets[moduleName]['notes'][temp.note])
    } else {
      setAdditionalNote()
    }
  }

  const getUserConfirmationDeleteInfraction = () => {
    return new Promise(resolve => {
      AntModal.confirm({
        title: `Delete Infraction?`,
        content: (
          <div>
            <span>Are you sure you want to delete this infraction?</span>
          </div>
        ),
        okText: 'Delete',
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
        okButtonProps: { 'aria-label': 'Confirm Delete' }
      })
    })
  }

  return (
    <AntModal
      title="Add Alert"
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      styles={{ maks: { pointerEvents: 'none' } }}
      confirmLoading={submitting}
      footer={null}
      wrapClassName={'allow-pass-through'}
    >
      <Form layout="vertical" onFinish={form.handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <Controller
            name="fieldName"
            rules={{ required: true, minLength: 1 }}
            render={({ field }) => (
              <Form.Item
                label="Field Name"
                htmlFor="fieldNameDropdown"
                {...getErrorProps(field.name, form.errors)}
              >
                <Select
                  id="fieldNameDropdown"
                  showSearch
                  filterOption={getDefaultFilter(filteredFieldOptions)}
                  {...field}
                  ref={ref => {
                    field.ref = ref
                    firstFieldRef.current = ref
                  }}
                  onChange={handleFieldNameChange}
                >
                  {filteredFieldOptions.map(({ label, path, infractions }) => (
                    <Select.Option
                      role="option"
                      key={path}
                      value={path}
                      aria-label={label}
                    >
                      <InfractableSelectOption infraction={infractions?.[0]}>
                        {label}
                      </InfractableSelectOption>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          />

          <Controller
            rules={{ required: true }}
            name="messagePresets"
            render={({ field }) => (
              <>
                <Form.Item
                  label="Message Presets"
                  htmlFor="presetDropdown"
                  {...getErrorProps(field.name, form.errors)}
                >
                  <Select
                    id="presetDropdown"
                    {...field}
                    onChange={(v, o) => {
                      handleMessagePresetsChange(v, o)
                      field.onChange(v, o)
                    }}
                    options={messagePresetsOptions}
                    listHeight={288}
                  />
                </Form.Item>
                {additionalNote && <p className="warning">{additionalNote}</p>}
              </>
            )}
          />
          <Controller
            name="message"
            rules={{
              required: true
            }}
            render={({ field }) => (
              <Form.Item
                label="Message"
                {...getErrorProps(field.name, form.errors)}
              >
                <Input.TextArea
                  rows={3}
                  readOnly={true}
                  style={{
                    backgroundColor: localStorage.getItem('darkMode')
                      ? colours.DARK_GREY
                      : colours.CONTROL_PANEL_GREY
                  }}
                  tabIndex="-1"
                  {...field}
                />
              </Form.Item>
            )}
          />

          {currentInfractions.length > 0 && (
            <Col>
              <Typography.Title type="danger" level={5}>
                Warning: an infraction will be overwritten
              </Typography.Title>

              {currentInfractions.map(infraction => {
                return (
                  <div
                    key={infraction.infractionId}
                    id={`infraction${infraction.infractionId}`}
                  >
                    <Typography.Text>
                      {infraction.isPending ? 'Pending: ' : 'Live: '}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {infraction.eventDescriptor ?? 'No Message'}
                    </Typography.Text>
                  </div>
                )
              })}
            </Col>
          )}

          <Divider type="horizontal" />

          <Col align="right">
            <Button type="primary" htmlType="submit" disabled={submitDisabled}>
              Create or Update
            </Button>
            <Button
              type="default"
              onClick={handleDelete}
              disabled={submitDisabled}
              danger
              icon={<Icon.DeleteOutlined />}
              style={{ marginLeft: '20px' }}
              aria-label="Delete"
            >
              Delete
            </Button>
          </Col>
        </FormProvider>
      </Form>
    </AntModal>
  )
}

AddInfractionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
}

// TODO: make this markup nicer
function InfractableSelectOption({ children, infraction = null, ...props }) {
  return (
    <div {...props} style={{ display: 'flex', flexDirection: 'row' }}>
      <div
        style={{ flex: '1 1 auto', overflow: 'hidden', marginRight: '0.25rem' }}
      >
        <Typography.Text>{children}</Typography.Text>
      </div>
      <Typography.Text type="danger">
        {!!infraction && <Icon.FlagFilled />}
      </Typography.Text>
    </div>
  )
}

InfractableSelectOption.propTypes = {
  children: PropTypes.node,
  infraction: PropTypes.object
}

function getErrorProps(name, errors) {
  const error = get(errors, name, null)

  if (error) {
    return {
      hasFeedback: true,
      validateStatus: 'error'
    }
  } else {
    return {}
  }
}

function getDefaultFilter(options) {
  return (input, option) =>
    options
      .find(opt => opt.path === option.key)
      ?.label.toLowerCase()
      .indexOf(input.toLowerCase()) >= 0
}

export function _calculatePayload(infractionFormData, infractionOpts) {
  return [
    {
      fieldName: infractionOpts.fieldNameAlias || infractionFormData.fieldName,
      fieldNameAlias: infractionOpts.fieldNameAlias,
      message: infractionFormData.message
    }
  ]
}
